import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import toast from "react-hot-toast";

import { ChatComposer } from "@/components/chat/ChatComposer";
import { ChatTurn } from "@/components/chat/ChatTurn";
import { ClarificationCard } from "@/components/chat/ClarificationCard";
import { PlanReviewCard } from "@/components/chat/PlanReviewCard";
import { ThinkingPanel } from "@/components/chat/ThinkingPanel";
import { SectionHead } from "@/components/editorial/SectionHead";
import { useArtifactDrawer } from "@/components/layout/artifactDrawerContext";
import { useChatStream } from "@/hooks/useChatStream";
import {
  deleteSessionAttachment,
  getSessionAttachments,
} from "@/services/api/sessions";

export function Chat() {
  const { getAccessTokenSilently } = useAuth0();
  const {
    requestedSession,
    sessionId,
    exchanges,
    isStreaming,
    hydrating,
    ask,
    pendingPlan,
    setPendingPlan,
    pendingClarification,
    setPendingClarification,
    handlePlanApprove,
    handleClarifySubmit,
  } = useChatStream();

  // Publish everything this run produced to the shell's artifact drawer, so
  // the masthead can offer it without props threading through AppShell.
  // Newest first — the drawer opens on whatever was just built.
  const { setArtifacts: setDrawerArtifacts } = useArtifactDrawer();
  const runArtifacts = useMemo(
    () => [...exchanges].reverse().flatMap((ex) => ex.artifacts),
    [exchanges],
  );
  useEffect(() => {
    setDrawerArtifacts(runArtifacts);
  }, [runArtifacts, setDrawerArtifacts]);

  // §B-3b: render a chip strip for documents indexed into the current
  // session so the user can see what's "in scope" and remove items they
  // don't want the agent reading from. Disabled while streaming because
  // a delete mid-turn would invalidate live session_attachment_search
  // calls in flight.
  const attachmentsQuery = useQuery({
    queryKey: ["chat", "sessions", sessionId, "attachments"],
    queryFn: () => (sessionId ? getSessionAttachments(sessionId) : Promise.resolve([])),
    enabled: Boolean(sessionId),
    staleTime: 5_000,
  });

  const removeSessionAttachment = useCallback(
    async (artifactId: string) => {
      if (!sessionId || isStreaming) return;
      try {
        await deleteSessionAttachment(sessionId, artifactId);
        await attachmentsQuery.refetch();
        toast.success("Removed from this session");
      } catch {
        toast.error("Failed to remove document");
      }
    },
    [sessionId, isStreaming, attachmentsQuery],
  );

  const showWelcome = exchanges.length === 0 && !hydrating && !requestedSession;

  const composer = (
    <ChatComposer
      onSubmit={ask}
      disabled={isStreaming}
      isStreaming={isStreaming}
      sessionAttachments={(attachmentsQuery.data ?? []).map((a) => ({
        artifact_id: a.artifact_id,
        filename: a.filename,
        chunk_count: a.chunk_count,
      }))}
      onRemoveSessionAttachment={removeSessionAttachment}
    />
  );

  // A new chat has nothing to scroll, so the composer is the whole interface —
  // centre it rather than stranding it under a screen of empty space.
  if (showWelcome) {
    return (
      <div className="flex h-full items-center justify-center pb-16">
        {/* Exactly the transcript's wrapper, then inset past the avatar
            gutter — so the composer occupies the same column here as it does
            once a conversation exists, and does not move on the first send. */}
        <div className="mx-auto w-full max-w-workspace px-6">
          <div className="pl-gutter">{composer}</div>
        </div>
      </div>
    );
  }

  return (
    // Full-height column: the transcript scrolls, the composer does not.
    // AppShell hands the chat route an unpadded h-full slot for exactly this.
    <div className="flex h-full min-h-0 flex-col">
      {/* Full-width scroller so its scrollbar sits at the viewport edge; the
          column is centred by the wrapper inside it, not around it. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-workspace px-6">
        <div className="pt-8">
          <SectionHead
            label="Chat"
            italicSuffix="active conversation"
            count={exchanges.length || undefined}
          />
        </div>

        <div className="space-y-12 py-8">
        {exchanges.map((ex, idx) => (
          <div key={ex.id} className="space-y-4">
            <ChatTurn
              index={idx + 1}
              query={ex.query}
              answer={ex.answer}
              citations={ex.citations}
              artifacts={ex.artifacts}
              groundingScore={ex.groundingScore}
              isStreaming={ex.isStreaming}
              thinkingSlot={
                <ThinkingPanel
                  entries={ex.thinking}
                  isStreaming={ex.isStreaming}
                  startedAt={ex.startedAt}
                />
              }
            />
            <div className="ml-12 max-w-reading">
              {pendingPlan?.exchangeId === ex.id && sessionId && (
                <div className="mt-4">
                  <PlanReviewCard
                    plan={pendingPlan.plan}
                    rationale={pendingPlan.rationale}
                    initialAnswerMode={pendingPlan.answerMode}
                    sessionId={sessionId}
                    getAccessToken={() => getAccessTokenSilently().catch(() => null)}
                    onApprove={handlePlanApprove}
                    onCancel={() => setPendingPlan(null)}
                  />
                </div>
              )}
              {pendingClarification?.exchangeId === ex.id && sessionId && (
                <div className="mt-4">
                  <ClarificationCard
                    payload={pendingClarification.payload}
                    sessionId={sessionId}
                    getAccessToken={() => getAccessTokenSilently().catch(() => null)}
                    onSubmit={handleClarifySubmit}
                    onCancel={() => setPendingClarification(null)}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        </div>
        </div>
      </div>

      {/* flex-none, outside the scroll region — the transcript scrolls up to
          the top edge of this and no further. */}
      {/* The wrapper mirrors the transcript's exactly, so the composer's left
          and right edges line up with the message text above it. pl-gutter
          skips the avatar column: alignment is to the text, not the avatars. */}
      <div className="flex-none border-t border-ruleline bg-parchment-100 py-4">
        <div className="mx-auto w-full max-w-workspace px-6">
          <div className="pl-gutter">{composer}</div>
        </div>
      </div>
    </div>
  );
}
